// Cola de cambios que no se pudieron guardar por falta de conexión (mover etapa, descartar, nota).
// Regla: nada de lo que el cliente hizo se pierde. Si la red falla (ya con reintentos), el cambio se queda en
// pantalla, se guarda en localStorage (sobrevive a recargar la página) y se manda solo al reconectar.
//  - Un cambio por candidato y tipo: el más nuevo reemplaza al anterior (mover dos veces = manda el último).
//  - Si al mandarlo la base lo rechaza por negocio (permiso, pago), se tira y se avisa; si es la red, se espera.
//  - Las lecturas de candidatos pasan por `conPendientes`, así lo recién cargado ya trae lo que falta mandar.
import { useSyncExternalStore } from "react";
import { toast } from "sonner";
import { columnaDe, primerNombre } from "@/reclutamiento/logica";
import type { CandidatoPortal, Etapa } from "@/reclutamiento/tipos";
import type { PortalApi } from "./api";
import { olvidarFicha } from "./precarga";
import { alReconectar, esErrorDeRed } from "./red";

export type Op =
  | { tipo: "etapa"; id: string; nombre: string | null; etapa: Exclude<Etapa, "descartado"> }
  | { tipo: "descartar"; id: string; nombre: string | null; motivo: string }
  | { tipo: "nota"; id: string; nombre: string | null; nota: string };
type Entrada = Op & { clave: string; t: number };

/** Etapa y descarte comparten clave: lo último que decidiste sobre la etapa es lo que vale. */
const claveDe = (op: Op) => (op.tipo === "nota" ? "nota:" : "estado:") + op.id;

export type EstadoPendientes = {
  ops: Entrada[];
  /** Mandando la cola (o un cambio) ahora mismo. */
  guardando: boolean;
  /** Se acaba de vaciar la cola: «Guardado» por unos segundos. */
  guardadoEn: number | null;
};
let estado: EstadoPendientes = { ops: [], guardando: false, guardadoEn: null };
const oyentes = new Set<() => void>();
let dueno: string | null = null;
let apiActual: PortalApi | null = null;

function poner(cambio: Partial<EstadoPendientes>) {
  estado = { ...estado, ...cambio };
  if ("ops" in cambio) persistir();
  oyentes.forEach((f) => f());
}
const claveLs = () => "portal-pendientes:" + dueno;
function persistir() {
  if (!dueno) return;
  try {
    if (estado.ops.length) localStorage.setItem(claveLs(), JSON.stringify(estado.ops));
    else localStorage.removeItem(claveLs());
  } catch {
    /* sin almacenamiento: la cola vive en memoria mientras la página esté abierta */
  }
}
function leerGuardadas(): Entrada[] {
  try {
    const t = localStorage.getItem(claveLs());
    const v = t ? (JSON.parse(t) as Entrada[]) : [];
    return Array.isArray(v) ? v.filter((x) => x && typeof x.id === "string" && typeof x.clave === "string") : [];
  } catch {
    return [];
  }
}

export const leerPendientes = () => estado;
export function suscribirPendientes(f: () => void) {
  oyentes.add(f);
  return () => void oyentes.delete(f);
}
export const usePendientes = () => useSyncExternalStore(suscribirPendientes, leerPendientes, leerPendientes);

/**
 * Arranca la cola para este usuario (la cola es de cada usuario y cliente: si otro entra en el mismo
 * navegador, no manda los cambios de alguien más). Devuelve cómo apagarla.
 */
export function iniciarPendientes(api: PortalApi, duenoNuevo: string): () => void {
  apiActual = api;
  dueno = duenoNuevo;
  estado = { ops: leerGuardadas(), guardando: false, guardadoEn: null };
  oyentes.forEach((f) => f());
  void vaciar();
  const apagar = alReconectar(() => {
    if (estado.ops.length) void vaciar();
  });
  return () => {
    apagar();
    if (apiActual === api) apiActual = null;
  };
}

const ejecutar = (api: PortalApi, op: Op) => {
  olvidarFicha(op.id);
  if (op.tipo === "etapa") return api.moverEtapa(op.id, op.etapa);
  if (op.tipo === "descartar") return api.descartar(op.id, op.motivo);
  return api.guardarNota(op.id, op.nota);
};

const quitar = (clave: string, solo?: Entrada) =>
  poner({ ops: estado.ops.filter((x) => x.clave !== clave || (solo && x !== solo)) });

/**
 * Guarda un cambio: lo intenta ya (con los reintentos de la red) y, si no hay conexión, lo deja en la cola.
 * «guardado» = llegó a la base; «en-cola» = se mandará solo al reconectar. Un error de negocio se lanza.
 */
export async function guardarCambio(api: PortalApi, op: Op): Promise<"guardado" | "en-cola"> {
  const clave = claveDe(op);
  quitar(clave); // lo nuevo reemplaza lo que estaba esperando
  const entrada: Entrada = { ...op, clave, t: Date.now() };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    poner({ ops: [...estado.ops, entrada] });
    return "en-cola";
  }
  poner({ guardando: true });
  try {
    await ejecutar(api, op);
    return "guardado";
  } catch (e) {
    if (!esErrorDeRed(e)) throw e;
    // Si mientras tanto hiciste otro cambio al mismo candidato, gana ese.
    poner({ ops: estado.ops.some((x) => x.clave === clave) ? estado.ops : [...estado.ops, entrada] });
    return "en-cola";
  } finally {
    poner({ guardando: false });
  }
}

let vaciando = false;
/** Manda la cola en orden. Se detiene al primer error de red (el resto espera a la siguiente vuelta). */
export async function vaciar(): Promise<void> {
  const api = apiActual;
  if (!api || vaciando || !estado.ops.length) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  vaciando = true;
  poner({ guardando: true });
  let todo = true;
  try {
    for (const op of [...estado.ops]) {
      if (!estado.ops.includes(op)) continue; // ya lo reemplazó otro cambio
      try {
        await ejecutar(api, op);
        quitar(op.clave, op);
      } catch (e) {
        if (esErrorDeRed(e)) {
          todo = false;
          break;
        }
        quitar(op.clave, op);
        const quien = primerNombre(op.nombre ?? "") || "un candidato";
        const que = op.tipo === "nota" ? "la nota de" : "el cambio de";
        toast.error(`No se pudo guardar ${que} ${quien}: ${e instanceof Error ? e.message : "la base lo rechazó"}`);
      }
    }
  } finally {
    vaciando = false;
    poner({ guardando: false, guardadoEn: todo && !estado.ops.length ? Date.now() : estado.guardadoEn });
  }
}

/** Lo que falta mandar, aplicado encima de lo que llegó de la base (para que no «regrese» al estado viejo). */
export function aplicarPendientes<T extends CandidatoPortal>(k: T): T {
  let x = k;
  for (const op of estado.ops) {
    if (op.id !== k.id) continue;
    if (op.tipo === "nota") x = { ...x, nota_cliente: op.nota.trim() || null };
    else if (op.tipo === "descartar") x = { ...x, etapa: "descartado", columna: "descartado", motivo_cliente: op.motivo };
    else x = { ...x, etapa: op.etapa, columna: columnaDe(x.veredicto, op.etapa), motivo_cliente: null };
  }
  return x;
}

/** La API con las lecturas de candidatos ya «parchadas» con lo que falta mandar. */
export function conPendientes(api: PortalApi): PortalApi {
  return {
    ...api,
    candidatos: (v) => api.candidatos(v).then((l) => (estado.ops.length ? l.map(aplicarPendientes) : l)),
    candidato: (id) => api.candidato(id).then((f) => (estado.ops.length ? aplicarPendientes(f) : f)),
  };
}

/** ¿Hay un cambio de este tipo esperando para este candidato? */
export const hayPendiente = (s: EstadoPendientes, tipo: Op["tipo"], id: string) =>
  s.ops.some((x) => x.id === id && x.tipo === tipo);

/** Al salir: la cola de ESTE usuario se queda en su navegador (se manda la próxima vez que entre). */
export function detenerPendientes() {
  apiActual = null;
  dueno = null;
  estado = { ops: [], guardando: false, guardadoEn: null };
  oyentes.forEach((f) => f());
}
